import { useState } from "react"

import { LockClosedIcon } from "@heroicons/react/20/solid"
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/solid"
import { useNavigate } from "react-router-dom"

import type {
  SolarUtilityCompanyOptionDto,
  SolarUtilityRateOptionDto,
  SolarUtilitySideDto,
} from "~/api/dto/solar-proposal-utility.dto"
import useSolarProposalPricing from "~/api/hooks/useSolarProposalPricing"
import useSolarProposalSavings from "~/api/hooks/useSolarProposalSavings"
import useSolarProposalUtility from "~/api/hooks/useSolarProposalUtility"
import useUpdateSolarProposalUtility from "~/api/hooks/useUpdateSolarProposalUtility"
import {
  SOLAR_UTILITY_RESTORE_FAILED_CODE,
  SOLAR_UTILITY_SAVE_FAILED_CODE,
  solarProposalErrorCode,
} from "~/api/solar-proposal-errors"
import { ComboboxField, TextField } from "~/components/ui-components/v2"
import { parseAxiosError } from "~/utils/parseAxiosError"

import { solarProposalPath } from "./solar-setup-paths"
import {
  EMPTY_UTILITY_DRAFT,
  MAX_ESCALATION_PCT,
  solarUtilityFormState,
  type SolarUtilityDraft,
  type SolarUtilitySideDraft,
  type SolarUtilitySideKey,
  type SolarUtilitySideView,
} from "./solar-utility-form"
import {
  SolarSummaryStepFooter,
  type SolarSummaryStepMode,
} from "./summary/SolarSummaryStepFooter"

/**
 * What a save left stale, read back from the sections' refetched reads. Every
 * utility input feeds savings; pricing only follows the post-solar company.
 */
export const utilitySavedMessage = (stale: {
  savings: boolean
  pricing: boolean
}): string =>
  stale.savings && stale.pricing
    ? `Saved. Savings and pricing are now out of date.`
    : stale.savings
      ? `Saved. Savings are now out of date.`
      : stale.pricing
        ? `Saved. Pricing is now out of date.`
        : `Saved.`
export const UTILITY_ACCOUNT_FAILED_MESSAGE = `Couldn't update the utility account.`
export const UTILITY_BASELINE_OUT_OF_DATE_MESSAGE = `These defaults may not match the usage and rates saved now. Recalculate savings to update this.`

const SIDE_TITLES: Record<SolarUtilitySideKey, string> = {
  pre: `Before solar`,
  post: `After solar`,
}

/** Genability spells a tariff without a code as the literal `None`. */
const rateLabel = (rate: SolarUtilityRateOptionDto): string =>
  rate.tariffCode &&
  rate.tariffCode !== `None` &&
  rate.tariffCode !== rate.tariffName
    ? `${rate.tariffName} (${rate.tariffCode})`
    : rate.tariffName

/** Search matches the rate as shown, its name, or its utility code. */
const matchesRateQuery = (
  rate: SolarUtilityRateOptionDto,
  query: string,
): boolean => {
  const needle = query.trim().toLowerCase()
  return (
    needle === `` ||
    [rateLabel(rate), rate.tariffName, rate.tariffCode].some((text) =>
      text.toLowerCase().includes(needle),
    )
  )
}

/** What a failed save means to the seller; the form keeps their values. */
const saveErrorMessage = (error: unknown): string => {
  switch (solarProposalErrorCode(error)) {
    case SOLAR_UTILITY_SAVE_FAILED_CODE:
      return `${UTILITY_ACCOUNT_FAILED_MESSAGE} It was restored, so nothing changed. Try again.`
    case SOLAR_UTILITY_RESTORE_FAILED_CODE:
      return `${UTILITY_ACCOUNT_FAILED_MESSAGE} It could not be restored either, so save again before continuing.`
    default:
      return parseAxiosError(error, `The utility selection could not be saved.`)
  }
}

const formatDefault = (rate: number | null): string =>
  rate == null ? `none yet` : `$${rate.toFixed(3)}/kWh`

const ResetButton = ({
  onClick,
  children,
}: {
  onClick: () => void
  children: React.ReactNode
}): JSX.Element => (
  <button
    type="button"
    className="text-sm font-semibold text-muted underline hover:text-foreground"
    onClick={onClick}
  >
    {children}
  </button>
)

const UtilitySidePanel = ({
  sideKey,
  side,
  view,
  overrideError,
  monthlyBillError = null,
  utilityRateError = null,
  baselineOutOfDate,
  disabled,
  onDraft,
}: {
  sideKey: SolarUtilitySideKey
  side: SolarUtilitySideDto
  view: SolarUtilitySideView
  overrideError: string | null
  monthlyBillError?: string | null
  utilityRateError?: string | null
  /** The defaults below describe a savings run the stored inputs have moved past. */
  baselineOutOfDate: boolean
  disabled: boolean
  onDraft: (patch: Partial<SolarUtilitySideDraft>) => void
}): JSX.Element => {
  const company =
    side.companies.find((entry) => entry.lseId === view.lseId) ??
    // The saved company even when this side no longer offers it.
    (side.company?.lseId === view.lseId && side.company
      ? { ...side.company, defaultRateId: `` }
      : null)
  const title = SIDE_TITLES[sideKey]

  return (
    <section
      aria-label={title}
      data-testid={`solar-utility-${sideKey}`}
      className="space-y-4 rounded-xl border border-border bg-surface p-5"
    >
      <h3 className="text-xs font-semibold tracking-wide text-muted uppercase">
        {title}
      </h3>
      {baselineOutOfDate &&
      (view.editor === `contractedRate` ||
        view.editor === `monthlyBill` ||
        view.editor === `utilityRate`) ? (
        <p
          role="status"
          data-testid={`solar-utility-${sideKey}-baseline-out-of-date`}
          className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm text-foreground"
        >
          <ExclamationTriangleIcon
            aria-hidden="true"
            className="mt-0.5 size-4 shrink-0 text-warning"
          />
          {UTILITY_BASELINE_OUT_OF_DATE_MESSAGE}
        </p>
      ) : null}
      {side.companies.length === 0 ? (
        <p className="text-sm text-muted">
          No residential tariffs are available for this location.
        </p>
      ) : (
        <>
          <ComboboxField<SolarUtilityCompanyOptionDto>
            label="Utility company"
            portal={false}
            disabled={disabled}
            options={side.companies}
            value={company}
            placeholder="Select a utility company"
            getKey={(entry) => entry.lseId}
            getLabel={(entry) => entry.name}
            onChange={(entry) => {
              // Re-picking the current company keeps the rate and override.
              if (!entry || entry.lseId === view.lseId) return
              // Picking a company preselects its "Best match"; nothing saves
              // until Save. A contracted rate belongs to its company, so the
              // new one starts at its default.
              onDraft({
                lseId: entry.lseId,
                rateId: entry.defaultRateId,
                override: ``,
                monthlyBill: null,
                utilityRate: null,
              })
            }}
            emptyMessage="No utility companies match that search."
          />
          <ComboboxField<SolarUtilityRateOptionDto>
            label="Rate"
            portal={false}
            disabled={disabled || view.lseId == null}
            options={view.rates}
            value={view.rate}
            placeholder="Select a rate"
            getKey={(rate) => rate.id}
            getLabel={rateLabel}
            filter={matchesRateQuery}
            renderOption={(rate) => (
              <span className="flex items-center gap-2 whitespace-normal normal-case">
                <span>{rateLabel(rate)}</span>
                {rate.id === view.defaultRateId ? (
                  <span className="rounded-full bg-success/15 px-2 py-0.5 text-xs font-semibold text-success">
                    Best match
                  </span>
                ) : null}
              </span>
            )}
            onChange={(rate) => {
              if (!rate) return
              onDraft({
                rateId: rate.id,
                override: null,
                monthlyBill: null,
                utilityRate: null,
              })
            }}
            emptyMessage="No rates match that search."
          />
          {view.editor === `contractedRate` ? (
            <div className="space-y-2">
              <TextField
                label="Contracted rate ($/kWh)"
                inputMode="decimal"
                disabled={disabled}
                value={view.overrideText}
                placeholder={
                  view.defaultContractedRate == null
                    ? `Default`
                    : `Default ${view.defaultContractedRate.toFixed(3)}`
                }
                description={`Leave empty to use the default (${formatDefault(view.defaultContractedRate)}).`}
                error={overrideError ?? undefined}
                onChange={(event) => onDraft({ override: event.target.value })}
              />
              {!disabled && view.overrideText !== `` ? (
                <ResetButton onClick={() => onDraft({ override: `` })}>
                  Reset to default
                </ResetButton>
              ) : null}
            </div>
          ) : null}
          {view.editor === `monthlyBill` && view.monthlyBill ? (
            <div className="space-y-2">
              <TextField
                label="Monthly bill ($)"
                inputMode="decimal"
                disabled={disabled}
                value={view.monthlyBill.text}
                placeholder={`Default ${view.monthlyBill.defaultBill}`}
                description={`Sets the contracted rate. Leave empty to use the default ($${view.monthlyBill.defaultBill}/month).`}
                error={monthlyBillError ?? undefined}
                onChange={(event) =>
                  onDraft({ monthlyBill: event.target.value })
                }
              />
              <p className="text-sm text-muted" data-testid="effective-rate">
                Effective rate:{` `}
                <strong className="text-foreground">
                  ${view.monthlyBill.effectiveRate.toFixed(2)}/kWh
                </strong>
              </p>
              {!disabled && view.monthlyBill.text !== `` ? (
                <ResetButton onClick={() => onDraft({ monthlyBill: `` })}>
                  Reset to default
                </ResetButton>
              ) : null}
            </div>
          ) : null}
          {view.editor === `utilityRate` && view.utilityRate ? (
            <div className="space-y-2">
              <TextField
                label="Utility rate ($/kWh)"
                inputMode="decimal"
                disabled={disabled}
                value={view.utilityRate.text}
                placeholder={view.utilityRate.base.toFixed(4)}
                description={
                  view.utilityRate.custom
                    ? `Custom rate. Genability's rate is $${view.utilityRate.base.toFixed(3)}/kWh.`
                    : `Genability's rate for this tariff. Change it to use your own.`
                }
                error={utilityRateError ?? undefined}
                onChange={(event) =>
                  onDraft({ utilityRate: event.target.value })
                }
              />
              {!disabled && view.utilityRate.custom ? (
                <ResetButton onClick={() => onDraft({ utilityRate: `` })}>
                  Reset to Genability&apos;s rate
                </ResetButton>
              ) : null}
            </div>
          ) : null}
          {view.editor === `utilityRatePending` ? (
            <div className="space-y-2">
              <p className="text-sm text-muted">
                {view.rate?.id === side.rate?.id
                  ? `Recalculate savings to edit this tariff's utility rate.`
                  : `Save, then recalculate savings, to edit this tariff's utility rate.`}
                {view.pendingCustomRate
                  ? ` A custom rate adjustment is saved and still applies.`
                  : null}
              </p>
              {!disabled && view.pendingCustomRate ? (
                <ResetButton onClick={() => onDraft({ utilityRate: `` })}>
                  Reset to Genability&apos;s rate
                </ResetButton>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </section>
  )
}

/**
 * Utility Company & Rate: the pre- and post-solar company, rate and
 * contracted rate, and the rate escalation, each validated again by the
 * server. Save sends only what changed and recalculates nothing; what it left
 * out of date (savings, and pricing when the post-solar company changed) stays
 * so until someone recalculates it on purpose.
 * A financing lock makes the form read-only, with the server's reason.
 */
export const SolarUtilityStep = ({
  proposalId,
  summary,
}: {
  proposalId: string
  /**
   * The Summary's step card: its button reads Continue, or Save & Continue
   * once edited, and moves on after a save instead of offering the sections.
   */
  summary?: SolarSummaryStepMode
}): JSX.Element => {
  const navigate = useNavigate()
  const query = useSolarProposalUtility(proposalId)
  // The save refetches both before it reports, so these are the new statuses.
  const savingsQuery = useSolarProposalSavings(proposalId)
  const pricingQuery = useSolarProposalPricing(proposalId)
  const update = useUpdateSolarProposalUtility()
  const [draft, setDraft] = useState<SolarUtilityDraft>(EMPTY_UTILITY_DRAFT)
  const [saved, setSaved] = useState(false)

  if (!query.data) {
    return query.isError ? (
      <div
        role="alert"
        className="space-y-3 rounded-xl border border-danger/40 bg-danger/10 p-4"
      >
        <p className="text-sm text-foreground">
          {parseAxiosError(
            query.error,
            `Utility companies and rates could not be loaded.`,
          )}
        </p>
        <button
          type="button"
          className="text-sm font-semibold text-primary underline"
          onClick={() => void query.refetch()}
        >
          Retry
        </button>
      </div>
    ) : (
      <div
        role="status"
        aria-label="Loading utility companies and rates"
        className="h-40 animate-pulse rounded-xl bg-muted"
      />
    )
  }

  const utility = query.data
  const locked = utility.financingLocked
  const stale = {
    savings: savingsQuery.data?.savingsStatus === `out_of_date`,
    pricing: pricingQuery.data?.pricingStatus === `out_of_date`,
  }
  const form = solarUtilityFormState(utility, draft)
  const changed = Object.keys(form.body).length > 0

  const editSide =
    (key: SolarUtilitySideKey) =>
    (patch: Partial<SolarUtilitySideDraft>): void => {
      setSaved(false)
      setDraft((current) => ({
        ...current,
        [key]: { ...current[key], ...patch },
      }))
    }

  const save = (): void => {
    update.mutate(
      { proposalId, body: form.body },
      {
        onSuccess: () => {
          setDraft(EMPTY_UTILITY_DRAFT)
          if (summary) {
            summary.onContinue()
            return
          }
          setSaved(true)
        },
      },
    )
  }

  /** Closes setup and brings the section into view. */
  const goToSection = (sectionId: string): void => {
    navigate(solarProposalPath(proposalId))
    requestAnimationFrame(() =>
      document.getElementById(sectionId)?.scrollIntoView({ block: `start` }),
    )
  }

  return (
    <div className="flex flex-col gap-6" data-testid="solar-utility-step">
      {locked ? (
        <p
          role="status"
          className="flex items-start gap-2 rounded-xl border border-border bg-surface p-4 text-sm text-foreground"
        >
          <LockClosedIcon
            aria-hidden="true"
            className="mt-0.5 size-4 shrink-0 text-muted"
          />
          {utility.financingLockReason ??
            `Financing locks the utility, financing and equipment selections.`}
        </p>
      ) : (
        <p className="text-sm text-muted">
          The savings estimate runs on these tariffs. Saving doesn&apos;t
          recalculate anything.
        </p>
      )}

      {([`pre`, `post`] as const).map((key) => (
        <UtilitySidePanel
          key={key}
          sideKey={key}
          side={utility[key]}
          view={form[key]}
          overrideError={
            key === `pre` ? form.errors.preOverride : form.errors.postOverride
          }
          monthlyBillError={key === `pre` ? form.errors.preMonthlyBill : null}
          utilityRateError={key === `pre` ? form.errors.preUtilityRate : null}
          baselineOutOfDate={utility.baselineStatus === `out_of_date`}
          disabled={locked || update.isLoading}
          onDraft={editSide(key)}
        />
      ))}

      <TextField
        label="Rate escalation (%/yr)"
        inputMode="numeric"
        disabled={locked || update.isLoading}
        value={form.escalationText}
        description={`A whole percent from 0 to ${MAX_ESCALATION_PCT}.`}
        error={form.errors.escalation ?? undefined}
        onChange={(event) => {
          setSaved(false)
          setDraft((current) => ({ ...current, escalation: event.target.value }))
        }}
      />

      {update.isError ? (
        <p role="alert" className="text-sm text-danger">
          {saveErrorMessage(update.error)}
        </p>
      ) : null}

      {saved && !changed ? (
        <div
          role="status"
          className="space-y-2 rounded-xl border border-success/40 bg-success/10 p-4 text-sm text-foreground"
        >
          <p className="flex items-center gap-2 font-semibold text-success">
            <CheckCircleIcon aria-hidden="true" className="size-5" />
            {utilitySavedMessage(stale)}
          </p>
          {stale.savings || stale.pricing ? (
            <p className="flex flex-wrap gap-x-4 gap-y-1">
              {stale.savings ? (
                <button
                  type="button"
                  className="font-semibold text-primary underline"
                  onClick={() => goToSection(`savings`)}
                >
                  Go to savings
                </button>
              ) : null}
              {stale.pricing ? (
                <button
                  type="button"
                  className="font-semibold text-primary underline"
                  onClick={() => goToSection(`investment`)}
                >
                  Go to investment
                </button>
              ) : null}
            </p>
          ) : null}
        </div>
      ) : null}

      {summary ? (
        <SolarSummaryStepFooter
          changed={changed && !locked}
          busy={update.isLoading}
          disabled={
            changed && !locked
              ? !form.valid || form.pre.rate == null || form.post.rate == null
              : utility.pre.rate == null || utility.post.rate == null
          }
          onSave={save}
          onContinue={summary.onContinue}
        />
      ) : locked ? null : (
        <div>
          <button
            type="button"
            className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            disabled={!changed || !form.valid || update.isLoading}
            onClick={save}
          >
            {update.isLoading ? `Saving…` : `Save`}
          </button>
        </div>
      )}
    </div>
  )
}
